import { useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { subDays } from 'date-fns';
import {
  apiService,
  Schema,
  BatchSummary,
  SchemaDriftEventsResponse,
  ValidatorRunListResponse,
} from '../../services/api';
import { useProjectContext } from '../../context/ProjectContext';
import { formatDate, normalizeStatusForUi } from '../../utils/transformationHelpers';

export function getDraftReviewComments(draft: Schema) {
  const metadata = (draft.metadata ?? {}) as Record<string, any>;
  const commentsRaw = metadata.review_comments;
  if (!Array.isArray(commentsRaw)) return [];

  return commentsRaw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const record = entry as Record<string, any>;
      const commentId = typeof record.comment_id === 'string' ? record.comment_id : '';
      const message = typeof record.message === 'string' ? record.message : '';
      if (!commentId || !message) return null;
      return {
        comment_id: commentId,
        author: typeof record.author === 'string' ? record.author : 'unknown',
        created_at: typeof record.created_at === 'string' ? record.created_at : '',
        message,
      };
    })
    .filter(
      (entry): entry is { comment_id: string; author: string; created_at: string; message: string } =>
        Boolean(entry)
    );
}

export function getDraftCommentSummary(draft: Schema) {
  const comments = getDraftReviewComments(draft);
  if (comments.length === 0) {
    return { count: 0, latest: null as string | null };
  }
  const latest = comments
    .map((c) => c.created_at)
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  return { count: comments.length, latest: latest ?? null };
}

export function useDashboardData() {
  const {
    selectedProjectId,
    targetSchema,
    projectSummary,
    isLoadingSummary,
    refreshSummary,
  } = useProjectContext();

  // ---------- Schema drafts (deduplicated + sorted) ----------
  const schemaDrafts = useMemo(() => {
    const drafts = [
      ...(projectSummary?.pending_schemas ?? []),
      ...(projectSummary?.recent_schemas ?? []),
    ];
    const seen = new Set<string>();
    const unique: Schema[] = [];
    drafts.forEach((d) => {
      if (!seen.has(d.schema_id)) {
        seen.add(d.schema_id);
        unique.push(d);
      }
    });
    return unique.sort((a, b) => {
      const l = a.created_at ? new Date(a.created_at).getTime() : 0;
      const r = b.created_at ? new Date(b.created_at).getTime() : 0;
      return r - l;
    });
  }, [projectSummary]);

  // ---------- Schema-mismatch batches ----------
  const schemaMismatchBatches = useMemo(() => {
    if (!projectSummary) return [];
    const setId = projectSummary.schema?.schema_id ?? null;
    return projectSummary.batches.filter((b) => {
      const bSetId = b.target_schema_id ?? null;
      const bHash = b.schema_version_hash ?? null;
      if (setId && bSetId && bSetId !== setId) return true;
      if (setId && !bSetId) return true;
      return false;
    });
  }, [projectSummary]);

  // ---------- Batch categories ----------
  const batches = useMemo(
    () =>
      (projectSummary?.batches ?? []).map((batch) => ({
        ...batch,
        status: normalizeStatusForUi(batch.status),
      })),
    [projectSummary?.batches]
  );

  const needsAttentionBatches = useMemo(
    () => batches.filter((b) => ['mapped', 'reviewing', 'failed', 'error'].includes(b.status)),
    [batches]
  );

  const inProgressBatches = useMemo(
    () =>
      batches.filter((b) =>
        ['uploaded', 'pending', 'profiling', 'profiled', 'summarizing', 'summarized', 'ready_for_mapping', 'mapping', 'approved', 'transforming', 'loading'].includes(b.status)
      ),
    [batches]
  );

  const completedBatches = useMemo(
    () => batches.filter((b) => b.status === 'completed'),
    [batches]
  );

  // ---------- Quick stats ----------
  const transformationsThisWeek = useMemo(() => {
    const weekAgo = subDays(new Date(), 7);
    return batches.filter((b) => b.created_at && new Date(b.created_at) >= weekAgo).length;
  }, [batches]);

  const readyForReview = needsAttentionBatches.length;
  const completedCount = completedBatches.length;

  const successRate = useMemo(() => {
    const finished = batches.filter((b) => ['completed', 'failed', 'error'].includes(b.status));
    if (finished.length === 0) return 100;
    return Math.round((completedCount / finished.length) * 100);
  }, [batches, completedCount]);

  // ---------- Schema drift ----------
  const [selectedDriftBatchId, setSelectedDriftBatchId] = useState('');

  useEffect(() => {
    if (!projectSummary || batches.length === 0) return;
    if (!selectedDriftBatchId) {
      const preferred = schemaMismatchBatches[0]?.batch_id ?? batches[0]?.batch_id ?? '';
      setSelectedDriftBatchId(preferred);
    }
  }, [projectSummary, schemaMismatchBatches, selectedDriftBatchId, batches]);

  const driftQuery = useQuery<SchemaDriftEventsResponse>(
    ['schema-drift', selectedDriftBatchId],
    () => apiService.getSchemaDrift(selectedDriftBatchId, { limit: 10 }),
    { enabled: Boolean(selectedDriftBatchId), refetchOnWindowFocus: false }
  );

  // ---------- Validator telemetry ----------
  const [selectedTelemetryBatchId, setSelectedTelemetryBatchId] = useState('');

  useEffect(() => {
    if (!projectSummary || batches.length === 0) return;
    if (!selectedTelemetryBatchId) {
      setSelectedTelemetryBatchId(batches[0]?.batch_id ?? '');
    }
  }, [projectSummary, selectedTelemetryBatchId, batches]);

  const telemetryQuery = useQuery<ValidatorRunListResponse>(
    ['validator-telemetry', selectedTelemetryBatchId],
    () => apiService.getValidatorTelemetry(selectedTelemetryBatchId, { limit: 10 }),
    { enabled: Boolean(selectedTelemetryBatchId), refetchOnWindowFocus: false }
  );

  // ---------- System stats ----------
  const { data: stats, isLoading: statsLoading } = useQuery(
    'stats',
    () => apiService.getStats(),
    { refetchInterval: 60_000, retry: 1, onError: () => ({ total_batches: 0, processed_files: 0 }) }
  );

  return {
    // Context forwarded
    selectedProjectId,
    targetSchema,
    projectSummary,
    isLoadingSummary,
    refreshSummary,

    // Computed
    schemaDrafts,
    schemaMismatchBatches,
    batches,
    needsAttentionBatches,
    inProgressBatches,
    completedBatches,

    // Stats
    transformationsThisWeek,
    readyForReview,
    completedCount,
    successRate,
    stats,
    statsLoading,

    // Drift
    selectedDriftBatchId,
    setSelectedDriftBatchId,
    driftQuery,

    // Telemetry
    selectedTelemetryBatchId,
    setSelectedTelemetryBatchId,
    telemetryQuery,

    // Helpers
    formatTimestamp: (v?: string | null) => formatDate(v, 'MMM dd, yyyy HH:mm'),
  };
}
